/* eslint-disable max-lines */
import {
  UnifiedAlarm,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { setDataVersionParam } from '@hakimo-ui/hakimo/util';
import { useMemo, useRef, useState } from 'react';
import { alarmColor } from '../../utils/alarmColor';
import {
  useAlarmGroupCamera,
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from '../alarm-provider/AlarmGroupProvider';
import { TIMELINE_HEIGHT } from '../constants';
import PreviewTooltip from './PreviewTooltip';
import { useClockPosition } from './useClockPosition';
import DetectionMarker from './DetectionMarker';
import { useFaceMarkers } from './useFaceMarkers';
import { bridgeGaps } from './utils';
import { useWeaponMarkers } from './useWeaponMarkers';
dayjs.extend(utc);

interface Props {
  cameraId: string;
  theme?: 'system' | 'dark';
  viewStart?: number;
  viewEnd?: number;
  showSeekerIndicator?: boolean;
  showAlarmGroupBounds?: boolean;
}

export function CameraTimeline(props: Props) {
  const {
    theme = 'dark',
    cameraId,
    viewStart,
    viewEnd,
    showSeekerIndicator = false,
    showAlarmGroupBounds = false,
  } = props;

  const { location, product, clock, dataVersion } = useAlarmGroupStatic();
  const {
    timelineBounds: {
      totalTimelineStartTime,
      totalTimelineEndTime,
      alarmGroupStartTime,
      alarmGroupEndTime,
    },
  } = useAlarmGroupPlayback();
  const { seekTo } = useAlarmGroupTime();
  const { camToDetailsMap } = useAlarmGroupCamera();

  // Use zoom view bounds when provided, otherwise fall back to full timeline bounds
  const effectiveStart = viewStart ?? totalTimelineStartTime;
  const effectiveEnd = viewEnd ?? totalTimelineEndTime;

  const { alarms } = camToDetailsMap[cameraId];
  const weaponMarkers = useWeaponMarkers(alarms);
  const faceMarkers = useFaceMarkers(alarms);

  const timelineRef = useRef<HTMLDivElement>(null);
  const seekerIndicatorRef = useRef<HTMLDivElement>(null);
  // State for preview tooltip (when hovering on a tick)
  const [hoverPreviewPosition, setHoverPreviewPosition] = useState<
    number | null
  >(null);
  const [hoverAlarm, setHoverAlarm] = useState<UnifiedAlarm | null>(null);

  const timelineDuration = useMemo(() => {
    return effectiveEnd - effectiveStart;
  }, [effectiveEnd, effectiveStart]);

  // Drive the media-box seeker indicator from the clock at 60fps (see
  // useClockPosition). Hidden when the playhead scrolls out of the view window.
  useClockPosition(
    clock,
    effectiveStart,
    effectiveEnd,
    showSeekerIndicator,
    (pct) => {
      const el = seekerIndicatorRef.current;
      if (!el) return;
      if (pct < 0 || pct > 100) {
        el.style.display = 'none';
      } else {
        el.style.display = '';
        el.style.left = `${pct}%`;
      }
    }
  );

  // Bridge held-frame gaps so the timeline matches what the player shows (see
  // bridgeGaps). Keyed by alarm id since the render below isn't sorted.
  const barEndById = useMemo(() => {
    const ends = new Map<string, number>();
    const hasFrames = alarms.some((a) => a.media?.type === 'frames');
    if (!hasFrames) {
      alarms.forEach((a) => ends.set(a.id, a.alarmEndTime));
      return ends;
    }
    const sorted = [...alarms].sort(
      (a, b) => a.alarmStartTime - b.alarmStartTime
    );
    const barEnds = bridgeGaps(
      sorted,
      (a) => a.alarmStartTime,
      (a) => a.alarmEndTime
    );
    sorted.forEach((a, i) => ends.set(a.id, barEnds[i]));
    return ends;
  }, [alarms]);

  const onHandleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.metaKey || e.ctrlKey) {
      return;
    }
    const el = timelineRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const clickX = e.clientX - rect.left;

    const clampedX = Math.max(0, Math.min(clickX, rect.width));
    const fraction = clampedX / rect.width;

    const clickedTime =
      effectiveStart + fraction * (effectiveEnd - effectiveStart);

    seekTo(clickedTime);
  };

  // Calculate left position (%) for an alarm tick
  const getAlarmPosition = (alarm: UnifiedAlarm) => {
    const diff = alarm.alarmStartTime - effectiveStart;
    return (diff / timelineDuration) * 100;
  };

  // Handler for hovering over an alarm tick.
  const onHoverTick =
    (alarm: UnifiedAlarm) => (e: React.MouseEvent<HTMLDivElement>) => {
      if (timelineRef.current) {
        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        setHoverPreviewPosition(percentage);
        setHoverAlarm(alarm);
      }
    };

  const onMouseLeaveTick = () => {
    setHoverPreviewPosition(null);
    setHoverAlarm(null);
  };

  const onAlarmClick =
    (alarmId: string, timestamp: number) =>
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.metaKey || e.ctrlKey) {
        const searchParams = new URLSearchParams();
        if (product === UnifiedMonitoringProduct.SCAN) {
          searchParams.set('product', 'scan');
        } else {
          const date = dayjs.utc(timestamp);
          const partitionKey = date.format('YYYYMM');
          searchParams.set('partitionKey', partitionKey);
          setDataVersionParam(searchParams, dataVersion);
        }
        const url = `/alarms/${alarmId}?${searchParams.toString()}`;
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      // TODO: check if this is required
      // onChangeCurrentAlarm?.(alarmId);
      return;
    };

  return (
    <>
      {/* Preview tooltip for the hovered alarm tick */}
      {hoverPreviewPosition !== null && hoverAlarm && (
        <PreviewTooltip
          position={hoverPreviewPosition}
          timelineRef={timelineRef}
          alarm={hoverAlarm}
          timezone={location?.timezone ?? 'UTC'}
        />
      )}
      <div className="relative">
        <div
          ref={timelineRef}
          className={clsx(
            'relative cursor-pointer overflow-hidden rounded',
            theme === 'system'
              ? 'bg-onlight-bg-3 dark:bg-ondark-bg-3/80'
              : 'bg-ondark-bg-1/80'
          )}
          style={{ height: `${TIMELINE_HEIGHT}px` }}
          onClick={onHandleTimelineClick}
        >
          {alarms.map((alarm) => {
            const left = getAlarmPosition(alarm);
            const barEnd = barEndById.get(alarm.id) ?? alarm.alarmEndTime;
            const width =
              ((barEnd - alarm.alarmStartTime) / timelineDuration) * 100;

            return (
              <div
                key={alarm.id}
                onMouseOverCapture={onHoverTick(alarm)}
                onMouseLeave={onMouseLeaveTick}
                className={clsx(
                  'absolute top-0 h-full rounded-e-md transition-transform',
                  alarmColor(alarm)
                )}
                style={{ left: `${left}%`, width: `${width}%` }}
                tabIndex={1}
                onClick={onAlarmClick(alarm.id, alarm.alarmStartTime)}
              ></div>
            );
          })}

          {weaponMarkers.map((marker) => {
            const markerLeft =
              ((marker.absoluteTime - effectiveStart) / timelineDuration) * 100;
            return (
              <DetectionMarker
                key={`weapon-${marker.alarmId}-${marker.frame}`}
                leftPercent={markerLeft}
                height={TIMELINE_HEIGHT}
                label="Weapon detected"
                onClick={() => seekTo(marker.absoluteTime)}
              />
            );
          })}

          {faceMarkers.map((marker) => {
            const markerLeft =
              ((marker.absoluteTime - effectiveStart) / timelineDuration) * 100;
            return (
              <DetectionMarker
                key={`face-${marker.alarmId}-${marker.profileId}-${marker.absoluteTime}`}
                leftPercent={markerLeft}
                height={TIMELINE_HEIGHT}
                label={marker.label}
                onClick={() => seekTo(marker.absoluteTime)}
              />
            );
          })}
        </div>

        {showAlarmGroupBounds &&
          timelineDuration > 0 &&
          [alarmGroupStartTime, alarmGroupEndTime].map((t, i) => {
            const percent = ((t - effectiveStart) / timelineDuration) * 100;
            if (percent < 0 || percent > 100) return null;
            return (
              <div
                key={`alarm-bound-${i}`}
                className="pointer-events-none absolute top-0 z-10 h-full"
                style={{ left: `${percent}%` }}
              >
                <div className="bg-primary-500 absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2" />
                <div className="border-t-primary-500 absolute -top-1 left-1/2 h-0 w-0 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent" />
              </div>
            );
          })}

        {showSeekerIndicator && timelineDuration > 0 && (
          <div
            ref={seekerIndicatorRef}
            className="pointer-events-none absolute top-0 z-10 h-full"
          >
            <div className="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 bg-white" />
            <div className="absolute -top-1 left-1/2 h-0 w-0 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent border-t-white" />
          </div>
        )}
      </div>
    </>
  );
}

export default CameraTimeline;
